/** Applicable statutes per jurisdiction, from the deterministic legal-basis
 *  matrix resolved at Gate 1 — not from the model, and not from RAG.
 */
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Scale, TriangleAlert, ShieldAlert } from 'lucide-react';
import { getContractTypeSchema } from '@/pages/contracts/contractTypeSchema';
import { AiLawList } from './AiLawList';
import { isBreached, lawCitation, type AiLaw } from './lawCompliance';

interface LegalBasisEntry {
  jurisdiction?: string;
  legal_basis?: string;
  harmonization?: string;
}

export interface JurisdictionContext {
  declared_contract_type?: string | null;
  detected_contract_type?: string;
  effective_contract_type?: string;
  type_mismatch?: boolean;
  party_jurisdictions?: string[];
  blocs?: string[];
  unresolved_jurisdictions?: string[];
  legal_bases?: LegalBasisEntry[];
  harmonization?: string;
  is_cross_border?: boolean;
  law_mismatch?: boolean;
  summary?: string;
  ai_laws?: AiLaw[];
}

const BLOC_LABELS: Record<string, string> = { EU: 'European Union', UK: 'United Kingdom', US: 'United States' };

const HARMONIZATION_STYLE: Record<string, string> = {
  high: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/30',
  medium: 'bg-sky-500/10 text-sky-700 dark:text-sky-400 border-sky-500/30',
  low: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30',
  very_low: 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/30',
};

const HARMONIZATION_LABELS: Record<string, string> = {
  high: 'High convergence', medium: 'Medium convergence',
  low: 'Low convergence', very_low: 'Very low convergence',
};

export function LegalBasisPanel({ context }: { context: JurisdictionContext | null | undefined }) {
  if (!context) return null;

  const bases = context.legal_bases ?? [];
  const unresolved = context.unresolved_jurisdictions ?? [];
  const typeLabel = getContractTypeSchema(context.effective_contract_type).label;
  // Surfaced above the list as well as on each card: a reviewer scanning the
  // gate should not have to read every citation to learn one of them failed.
  const breached = (context.ai_laws ?? []).filter(isBreached);

  return (
    <Card className="border-border">
      <CardContent className="space-y-3 px-4 pt-3 pb-3 text-xs">
        <div className="flex items-center gap-2">
          <Scale className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <span className="text-sm font-medium">Applicable law — {typeLabel}</span>
          {context.harmonization && (
            <Badge
              variant="outline"
              className={`ml-auto text-[10px] ${HARMONIZATION_STYLE[context.harmonization] ?? ''}`}
            >
              {HARMONIZATION_LABELS[context.harmonization] ?? context.harmonization}
            </Badge>
          )}
        </div>

        {context.summary && <p className="text-muted-foreground">{context.summary}</p>}

        {breached.length > 0 && (
          <p className="flex items-start gap-2 rounded border border-red-500/30 bg-red-500/10 p-2 text-red-800 dark:text-red-300">
            <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span>
              The contract was flagged as not complying with{' '}
              {breached.length === 1 ? 'one identified law' : `${breached.length} identified laws`}:{' '}
              {breached.map(lawCitation).join('; ')}.
              Approval requires a senior reviewer.
            </span>
          </p>
        )}

        {bases.length > 0 && (
          <ul className="space-y-1.5" aria-label="Reference table">
            {bases.map((entry) => (
              <li key={entry.jurisdiction} className="flex gap-2">
                <Badge variant="outline" className="h-5 shrink-0 text-[10px]">
                  {entry.jurisdiction}
                </Badge>
                <span>
                  <span className="text-muted-foreground">
                    {BLOC_LABELS[entry.jurisdiction ?? ''] ?? entry.jurisdiction} —{' '}
                  </span>
                  {entry.legal_basis}
                </span>
              </li>
            ))}
          </ul>
        )}

        <AiLawList laws={context.ai_laws} />

        {context.law_mismatch && (
          <p className="flex items-start gap-2 rounded border border-amber-500/30 bg-amber-500/10 p-2 text-amber-800 dark:text-amber-300">
            <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span>
              The parties sit in jurisdictions whose legal bases diverge for this contract type.
              Approval requires a senior reviewer.
            </span>
          </p>
        )}

        {unresolved.length > 0 && (
          <p className="text-muted-foreground">
            Could not place: {unresolved.join(', ')}. These parties are excluded from the
            comparison — the contract may span jurisdictions not shown here.
          </p>
        )}

        <p className="border-t pt-2 text-[10px] text-muted-foreground">
          Convergence ratings are a research-derived estimate of how far these legal regimes
          align, not an official classification, and are not legal advice.
        </p>
      </CardContent>
    </Card>
  );
}
